import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, Box, useMediaQuery, useTheme, Alert } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { FormMentionTextField, SaveIndicator } from '@personal-dashboard/ui';
import { useAutosave } from '../../../hooks/useAutosave';
import { GET_PEOPLE } from '../../People/queries';
import { getLinkedPeopleIds } from '../../../utils/mention';
import { LinkedPeopleChips } from './LinkedPeopleChips';

const GET_JOURNAL_ENTRIES = gql`
  query GetJournalEntries($dateRange: DateRange!) {
    getJournalEntries(dateRange: $dateRange) {
      id
      content
      date
      createdAt
      updatedAt
      people {
        id
        name
      }
    }
  }
`;

const CREATE_JOURNAL_ENTRY = gql`
  mutation CreateJournalEntry($entry: JournalEntryInput!) {
    createJournalEntry(entry: $entry) {
      id
      content
      date
      createdAt
      updatedAt
      people {
        id
        name
      }
    }
  }
`;

const UPDATE_JOURNAL_ENTRY = gql`
  mutation UpdateJournalEntry($id: ID!, $entry: JournalEntryInput!) {
    updateJournalEntry(id: $id, entry: $entry) {
      id
      content
      date
      createdAt
      updatedAt
      people {
        id
        name
      }
    }
  }
`;

interface JournalFormData {
  content: string;
}

interface DayInfoFormProps {
  selectedDate: Dayjs;
}

const DayInfoForm: React.FC<DayInfoFormProps> = ({ selectedDate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [entryId, setEntryId] = useState<string | null>(null);
  const entryIdRef = useRef<string | null>(null);

  const { data: peopleData, error: peopleError } = useQuery(GET_PEOPLE);
  const peopleList = peopleData?.getPeople || [];

  const methods = useForm<JournalFormData>({
    defaultValues: {
      content: '',
    },
  });

  const { reset, watch } = methods;
  const content = watch('content');

  const linkedPeople = React.useMemo(() => {
    const ids = getLinkedPeopleIds(content || '', peopleList);
    return peopleList.filter((p: any) => ids.includes(p.id));
  }, [content, peopleList]);

  const dateStart = selectedDate.format('YYYY-MM-DDT00:00:00.000Z');
  const dateEnd = selectedDate.format('YYYY-MM-DDT23:59:59.999Z');

  const { refetch } = useQuery(GET_JOURNAL_ENTRIES, {
    variables: { dateRange: { start: dateStart, end: dateEnd } },
    skip: true,
  });

  const [createEntry] = useMutation(CREATE_JOURNAL_ENTRY, {
    refetchQueries: ['GetCalendarData', 'GetStats']
  });
  const [updateEntry] = useMutation(UPDATE_JOURNAL_ENTRY, {
    refetchQueries: ['GetCalendarData', 'GetStats']
  });

  const saveJournal = useCallback(async (data: { content: string }) => {
    if (!data.content.trim() && !entryIdRef.current) return;

    const matchedPeopleIds = getLinkedPeopleIds(data.content, peopleList);

    const entryData = {
      content: data.content,
      date: selectedDate.format('YYYY-MM-DDT12:00:00.000Z'),
      people: matchedPeopleIds
    };

    if (entryIdRef.current) {
      await updateEntry({
        variables: { id: entryIdRef.current, entry: entryData },
      });
    } else {
      const result = await createEntry({
        variables: { entry: entryData },
      });
      const newId = result.data.createJournalEntry.id;
      setEntryId(newId);
      entryIdRef.current = newId;
    }
  }, [selectedDate, createEntry, updateEntry, peopleList]);

  const { status, setStatus, setManualData } = useAutosave({ content }, saveJournal);

  // Initial fetch for the selected date
  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const result = await refetch({ 
          dateRange: { start: dateStart, end: dateEnd } 
        });
        const entries = result.data?.getJournalEntries || [];
        if (entries.length > 0) {
          const entry = entries[0];
          setEntryId(entry.id);
          entryIdRef.current = entry.id;
          const initialValues = { content: entry.content };
          reset(initialValues);
          setManualData(initialValues);
        } else {
          setEntryId(null);
          entryIdRef.current = null;
          const initialValues = { content: '' };
          reset(initialValues);
          setManualData(initialValues);
        }
      } catch (error) {
        console.error('Error fetching entry:', error);
      }
    };
    fetchEntry();
  }, [selectedDate, refetch, reset, setManualData]);

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {peopleError && <Alert severity="error" sx={{ mb: 2 }}>Failed to load people: {peopleError.message}</Alert>}

        <Paper sx={{ p: 3, backgroundColor: 'background.paper', flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <SaveIndicator status={status} />
          </Box>

          <LinkedPeopleChips people={linkedPeople} />

          <FormMentionTextField
            name="content"
            label="How was your day?"
            placeholder="Write your thoughts for today..."
            multiline
            rows={isMobile ? 12 : 18}
            variant="outlined"
            fullWidth
            people={peopleList}
          />
        </Paper>
      </Box>
    </FormProvider>
  );
};

export default DayInfoForm;
